/**
 * @author: chenlv
 * @date: 2025/01/30
 * @description: 品类表格搜索组件
 */
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { SearchBox } from 'tea-component';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { buriedPointSearch } from '@home/utils/buriedPoint';

import './index.scss';

interface IGenreTableSearchProps extends IBaseProps {
  /** 搜索打点信息 */
  pageLogInfo?: { pageId: string; typeId: string };
  /** 当前搜索值 */
  value?: string;
  /** 搜索值变更回调 */
  onChange?: (value: string) => void;
}

/**
 * GenreTableSearch 品类表格搜索组件
 * @description 使用 tea-component 的 SearchBox 组件，Enter 键触发搜索
 */
const GenreTableSearch: React.FC<IGenreTableSearchProps> = (props) => {
  const { style, className, pageLogInfo, value = '', onChange } = props;

  const { formatMessage } = useIntlExtend();

  // 内部输入框状态
  const [inputValue, setInputValue] = useState(value);

  // 当外部 value 变化时，同步更新内部状态
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // 处理搜索框输入变化（只更新内部状态，不触发搜索）
  const handleChange = (newValue: string) => {
    setInputValue(newValue);
  };

  // 处理搜索（Enter 键触发）
  const handleSearch = (searchValue?: string) => {
    onChange?.(searchValue ?? '');
    // 添加搜索打点
    if (pageLogInfo?.pageId) {
      buriedPointSearch({ extInfo: pageLogInfo.pageId, typeId: pageLogInfo.typeId }, searchValue);
    }
  };

  // 处理清空搜索
  const handleClear = () => {
    const emptyValue = '';
    setInputValue(emptyValue);
    if (onChange) {
      onChange(emptyValue);
    }
  };

  return (
    <SearchBox
      style={style}
      className={classnames('dbi-genre-table-search', className)}
      placeholder={formatMessage({ id: 'dbi_casual_genre_table_search_placeholder' })}
      value={inputValue}
      onChange={handleChange}
      onSearch={handleSearch}
      onClear={handleClear}
    />
  );
};

export default GenreTableSearch;
